import React, { useState } from "react";
import styled from "styled-components";
import Photo from "../../components/Photo";
import ImageFileInput from "../../components/ImageFileInput";
import { FieldRow } from "../../components/forms";
import { Button, ColoredButton } from "../../components/buttons";
import { FormButtonsContainer } from "../../components/layout";

const TextInput = styled.input`
  min-width: 320px;
`;

const NumberInput = styled.input`
  width: 90px;
`;

const TextArea = styled.textarea`
  min-width: 420px;
  min-height: 220px;
  resize: vertical;
`;

const FieldValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const StatsCard = styled.div`
  margin-top: 18px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
`;

const StatsTitle = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 8px 18px;
`;

const Hint = styled.div`
  font-size: 12px;
  opacity: 0.7;
  max-width: 420px;
  text-align: right;
`;

const ExistingImage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const formatDistance = distanceMeters => {
  if (!distanceMeters && distanceMeters !== 0) {
    return "Unknown";
  }
  return `${(distanceMeters / 1000).toFixed(1)} km`;
};

const formatDuration = durationSeconds => {
  if (!durationSeconds && durationSeconds !== 0) {
    return "Unknown";
  }
  return `${Math.round(durationSeconds / 60)} min`;
};

const formatCoordinate = point => {
  if (!point) {
    return "Unknown";
  }
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return "Unknown";
  }
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

const RouteStats = ({ route }) => {
  if (!route || !route.route_payload) {
    return null;
  }

  const payload = route.route_payload;
  return (
    <StatsCard>
      <StatsTitle>Current Route</StatsTitle>
      <StatsGrid>
        <div>Distance: {formatDistance(route.distance_meters)}</div>
        <div>Duration: {formatDuration(route.duration_seconds)}</div>
        <div>Loop: {route.is_loop ? "Yes" : "No"}</div>
        <div>Points: {route.point_count || (payload.points || []).length}</div>
        <div>Start: {formatCoordinate(payload.start)}</div>
        <div>End: {formatCoordinate(payload.end)}</div>
        <div>Meeting point: {formatCoordinate(payload.meeting_point)}</div>
        <div>Quality score: {route.quality_score}</div>
      </StatsGrid>
    </StatsCard>
  );
};

const RouteForm = props => {
  const {
    onCancel,
    onSubmit,
    edit = false,
    route
  } = props;

  const [name, setName] = useState(route ? route.name || "" : "");
  const [city, setCity] = useState(route ? route.city || "" : "");
  const [qualityScore, setQualityScore] = useState(
    route ? String(route.quality_score || 85) : "85"
  );
  const [tags, setTags] = useState(
    route && route.tags ? route.tags.join(", ") : ""
  );
  const [isActive, setIsActive] = useState(
    route ? !!route.is_active : true
  );
  const [routeSource, setRouteSource] = useState("");

  const handleSubmit = e => {
    e.preventDefault();
    const fileInput = e.target.querySelector('[name="file"]');
    const file =
      fileInput && fileInput.files && fileInput.files[0]
        ? fileInput.files[0]
        : null;

    onSubmit({
      name: name.trim() || undefined,
      city: city.trim(),
      quality_score: Number(qualityScore || 85),
      tags: tags
        .split(",")
        .map(tag => tag.trim())
        .filter(Boolean),
      is_active: isActive,
      route_source: routeSource.trim() || undefined,
      file
    });
  };

  const currentHero = route && (route.hero_thumb_url || route.hero_image_url);

  return (
    <form onSubmit={handleSubmit}>
      <FieldRow>
        <label>Name{edit ? "" : " (optional)"}</label>
        <FieldValue>
          <TextInput
            name="name"
            value={name}
            required={edit}
            placeholder="Use GPX / KML name if left empty"
            onChange={e => setName(e.target.value)}
          />
        </FieldValue>
      </FieldRow>

      <FieldRow>
        <label>City</label>
        <FieldValue>
          <TextInput
            name="city"
            value={city}
            required
            placeholder="Barcelona"
            onChange={e => setCity(e.target.value)}
          />
        </FieldValue>
      </FieldRow>

      <FieldRow>
        <label>Quality score</label>
        <FieldValue>
          <NumberInput
            type="number"
            min="0"
            max="100"
            name="quality_score"
            value={qualityScore}
            onChange={e => setQualityScore(e.target.value)}
          />
        </FieldValue>
      </FieldRow>

      <FieldRow>
        <label>Tags</label>
        <FieldValue>
          <TextInput
            name="tags"
            value={tags}
            placeholder="waterfront, promenade, flat"
            onChange={e => setTags(e.target.value)}
          />
        </FieldValue>
      </FieldRow>

      <FieldRow>
        <label>Status</label>
        <FieldValue>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
            />
            Active
          </CheckboxRow>
        </FieldValue>
      </FieldRow>

      <FieldRow>
        <label>GPX / KML</label>
        <FieldValue>
          <TextArea
            name="route_source"
            value={routeSource}
            required={!edit}
            placeholder={
              edit
                ? "Paste a new GPX or KML only if you want to replace the current route."
                : "Paste the full GPX or KML route source here."
            }
            onChange={e => setRouteSource(e.target.value)}
          />
          <Hint>
            {edit
              ? "Leave this empty to keep the existing route geometry."
              : "The backend auto-detects GPX versus KML."}
          </Hint>
        </FieldValue>
      </FieldRow>

      <FieldRow>
        <label>Hero image</label>
        <FieldValue>
          {currentHero && (
            <ExistingImage>
              <Photo
                url={route.hero_image_url || route.hero_thumb_url}
                thumbnail_url={route.hero_thumb_url || route.hero_image_url}
              />
              <Hint>Current route image</Hint>
            </ExistingImage>
          )}
          <ImageFileInput name="file" />
          <Hint>Optional. Uploaded after the route itself is saved.</Hint>
        </FieldValue>
      </FieldRow>

      <RouteStats route={route} />

      {!edit ? (
        <div
          style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}
        >
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
          <ColoredButton type="submit">Create</ColoredButton>
        </div>
      ) : (
        <FormButtonsContainer>
          <ColoredButton type="submit">Save</ColoredButton>
        </FormButtonsContainer>
      )}
    </form>
  );
};

export default RouteForm;
